// ============================================================================
// Explicit renderer for independently booted authenticated surfaces
// ============================================================================
//
// There is deliberately no inferred route/component registry here. A ledger
// row is not render code: each released surface adds one exact renderer in its
// own reviewed slice. Returning null for every other id is fail-closed; App
// never treats an unknown release as a generic App route.
// ============================================================================

import React from 'react';

import BroilerDailysRouter from '../../broiler/BroilerDailysView.tsx';
import BroilerFeedView from '../../broiler/BroilerFeedView.tsx';
import BroilerListRouter from '../../broiler/BroilerListView.tsx';
import type {BroilerListProps} from '../../broiler/BroilerListView.tsx';
import BatchForm from '../../broiler/BatchForm.tsx';
import type {BatchFormFullscreenActions} from '../../broiler/BatchForm.tsx';
import BroilerHomeView from '../../broiler/BroilerHomeView.tsx';
import BroilerTimelineView from '../../broiler/BroilerTimelineView.tsx';
import CattleBatchesRouter from '../../cattle/CattleBatchesView.tsx';
import CattleBreedingView from '../../cattle/CattleBreedingView.tsx';
import CattleDailysRouter from '../../cattle/CattleDailysView.tsx';
import CattleHomeView from '../../cattle/CattleHomeView.tsx';
import CattleForecastView from '../../cattle/CattleForecastView.tsx';
import CattleHerdsRouter, {CattleHerdsHub} from '../../cattle/CattleHerdsView.tsx';
import CattleLogPage from '../../cattle/CattleLogPage.tsx';
import HomeDashboard from '../../dashboard/HomeDashboard.tsx';
import LightHomePortal from '../../dashboard/LightHomePortal.tsx';
import CattleWeighInsView from '../../cattle/CattleWeighInsView.tsx';
import EggDailysRouter from '../../layer/EggDailysView.tsx';
import {LayerBatchesHub} from '../../layer/LayerBatchesView.tsx';
import {LayerBatchRecordMount, LayerHousingRecordMount} from '../../layer/LayerBatchesView.tsx';
import LayerDailysRouter from '../../layer/LayerDailysView.tsx';
import LayersHomeView from '../../layer/LayersHomeView.tsx';
import LayersView from '../../layer/LayersView.tsx';
import BreedingView from '../../pig/BreedingView.tsx';
import FarrowingView from '../../pig/FarrowingView.tsx';
import PigBatchesView from '../../pig/PigBatchesView.tsx';
import PigDailysRouter from '../../pig/PigDailysView.tsx';
import SowsView from '../../pig/SowsView.tsx';
import PigsHomeView from '../../pig/PigsHomeView.tsx';
import SheepBatchesRouter, {SheepBatchesHub} from '../../sheep/SheepBatchesView.tsx';
import SheepFlocksRouter, {SheepFlocksHub} from '../../sheep/SheepFlocksView.tsx';
import SheepDailysRouter from '../../sheep/SheepDailysView.tsx';
import SheepHomeView from '../../sheep/SheepHomeView.tsx';
import SheepWeighInsView from '../../sheep/SheepWeighInsView.tsx';
import UnauthorizedRedirect from '../../shared/UnauthorizedRedirect.tsx';
import AddFeedWebform from '../../webforms/AddFeedWebform.tsx';
import WeighInsWebform from '../../webforms/WeighInsWebform.tsx';
import TasksWebform from '../../webforms/TasksWebform.tsx';
import FuelingHub from '../../webforms/FuelingHub.tsx';
import FuelSupplyWebform from '../../webforms/FuelSupplyWebform.tsx';
import {useBatches} from '../../contexts/BatchesContext.jsx';
import {useFeedCosts} from '../../contexts/FeedCostsContext.jsx';
import {useLayer} from '../../contexts/LayerContext.jsx';
import AddFeedAuthenticatedBridge from '../data/AddFeedAuthenticatedBridge.tsx';
import {PigDailysWebformMount} from '../data/PigDailysAuthenticatedBridge.tsx';
import TasksWebformAuthenticatedBridge from '../data/TasksWebformAuthenticatedBridge.tsx';
import {WebformHubAuthenticatedMount} from '../data/WebformHubAuthenticatedBridge.tsx';
import FuelingAuthenticatedBridge from '../data/FuelingAuthenticatedBridge.tsx';
import WeighInsAuthenticatedBridge from '../data/WeighInsAuthenticatedBridge.tsx';
import {RESOURCE} from '../data/routeDataCatalog.js';
import type {BroilerBatchFormActionStateData} from '../data/routeDataCatalog.js';
import {useRouteResource} from '../data/useRouteResource.js';
import {
  RELEASED_ACTIVITY_PATHNAME,
  RELEASED_ACTIVITY_SURFACE,
  RELEASED_ANIMAL_HISTORY_PATHNAME,
  RELEASED_ANIMAL_HISTORY_SURFACE,
  RELEASED_ADD_FEED_PATHNAME,
  RELEASED_ADD_FEED_SURFACE,
  RELEASED_BROILER_HOME_PATHNAME,
  RELEASED_BROILER_HOME_SURFACE,
  RELEASED_BROILER_BATCHES_DETAIL_SURFACE,
  RELEASED_BROILER_BATCHES_INDEX_PATHNAME,
  RELEASED_BROILER_BATCHES_INDEX_SURFACE,
  RELEASED_BROILER_BATCH_FORM_FULLSCREEN_SURFACE,
  RELEASED_BROILER_TIMELINE_PATHNAME,
  RELEASED_BROILER_TIMELINE_SURFACE,
  RELEASED_BROILER_FEED_PATHNAME,
  RELEASED_BROILER_FEED_SURFACE,
  RELEASED_PIG_FEED_PATHNAME,
  RELEASED_PIG_FEED_SURFACE,
  RELEASED_BROILER_WEIGHINS_PATHNAME,
  RELEASED_BROILER_WEIGHINS_SURFACE,
  RELEASED_CATTLE_BREEDING_PATHNAME,
  RELEASED_CATTLE_BREEDING_SURFACE,
  RELEASED_CLIENT_ERRORS_PATHNAME,
  RELEASED_CLIENT_ERRORS_SURFACE,
  RELEASED_WEBFORMS_ADMIN_PATHNAME,
  RELEASED_WEBFORMS_ADMIN_SURFACE,
  RELEASED_CATTLE_BATCHES_DETAIL_SURFACE,
  RELEASED_CATTLE_BATCHES_INDEX_PATHNAME,
  RELEASED_CATTLE_BATCHES_INDEX_SURFACE,
  RELEASED_CATTLE_FORECAST_BATCH_DETAIL_SURFACE,
  RELEASED_CATTLE_FORECAST_PATHNAME,
  RELEASED_CATTLE_FORECAST_SURFACE,
  RELEASED_CATTLE_DAILYS_DETAIL_SURFACE,
  RELEASED_CATTLE_DAILYS_INDEX_PATHNAME,
  RELEASED_CATTLE_DAILYS_INDEX_SURFACE,
  RELEASED_CATTLE_LOG_PATHNAME,
  RELEASED_CATTLE_LOG_SURFACE,
  RELEASED_CATTLE_HERDS_DETAIL_SURFACE,
  RELEASED_CATTLE_HERDS_INDEX_PATHNAME,
  RELEASED_CATTLE_HERDS_INDEX_SURFACE,
  RELEASED_CATTLE_HOME_PATHNAME,
  RELEASED_CATTLE_HOME_SURFACE,
  RELEASED_EQUIPMENT_CHECKLIST_ENTRY_SURFACE,
  RELEASED_EQUIPMENT_FLEET_DETAIL_SURFACE,
  RELEASED_EQUIPMENT_FUELING_ENTRY_SURFACE,
  RELEASED_EQUIPMENT_FUELING_FORM_SURFACE,
  RELEASED_CATTLE_WEIGHINS_PATHNAME,
  RELEASED_CATTLE_WEIGHINS_SURFACE,
  RELEASED_EQUIPMENT_FUELING_HUB_PATHNAME,
  RELEASED_EQUIPMENT_FUELING_HUB_SURFACE,
  RELEASED_EQUIPMENT_FUELING_SUPPLY_PATHNAME,
  RELEASED_EQUIPMENT_FUELING_SUPPLY_SURFACE,
  RELEASED_EQUIPMENT_FLEET_INDEX_SURFACE,
  RELEASED_FUEL_SUPPLY_PATHNAME,
  RELEASED_FUEL_SUPPLY_SURFACE,
  RELEASED_LAYER_BATCHES_INDEX_PATHNAME,
  RELEASED_LAYER_BATCHES_INDEX_SURFACE,
  RELEASED_LAYER_DAILYS_DETAIL_SURFACE,
  RELEASED_LAYER_DAILYS_INDEX_PATHNAME,
  RELEASED_LAYER_DAILYS_INDEX_SURFACE,
  RELEASED_LAYER_GROUPS_PATHNAME,
  RELEASED_LAYER_GROUPS_SURFACE,
  RELEASED_LAYERS_HOME_PATHNAME,
  RELEASED_LAYERS_HOME_SURFACE,
  RELEASED_LAYER_BATCHES_DETAIL_SURFACE,
  RELEASED_LAYER_HOUSINGS_DETAIL_SURFACE,
  RELEASED_LIGHT_HOME_PATHNAME,
  RELEASED_LIGHT_HOME_SURFACE,
  RELEASED_STANDARD_HOME_PATHNAME,
  RELEASED_STANDARD_HOME_SURFACE,
  RELEASED_MY_SUBMISSIONS_PATHNAME,
  RELEASED_MY_SUBMISSIONS_SURFACE,
  RELEASED_NEWSLETTER_ADMIN_PATHNAME,
  RELEASED_NEWSLETTER_ADMIN_SURFACE,
  RELEASED_PIG_DAILYS_WEBFORM_PATHNAME,
  RELEASED_PIG_DAILYS_WEBFORM_SURFACE,
  RELEASED_PIG_BATCHES_DETAIL_SURFACE,
  RELEASED_PIG_BATCHES_INDEX_PATHNAME,
  RELEASED_PIG_BATCHES_INDEX_SURFACE,
  RELEASED_WEBFORM_HUB_SURFACE,
  RELEASED_PIG_BREEDING_PATHNAME,
  RELEASED_PIG_BREEDING_SURFACE,
  isReleasedPigFarrowingPathname,
  RELEASED_PIG_FARROWING_SURFACE,
  RELEASED_PIG_SOWS_SURFACE,
  RELEASED_TASKS_WEBFORM_PATHNAME,
  RELEASED_TASKS_WEBFORM_SURFACE,
  RELEASED_PIG_WEIGHINS_PATHNAME,
  RELEASED_PIG_WEIGHINS_SURFACE,
  RELEASED_PIGS_HOME_PATHNAME,
  RELEASED_PIGS_HOME_SURFACE,
  RELEASED_PASTURE_MAP_PATHNAME,
  RELEASED_PASTURE_MAP_SURFACE,
  RELEASED_PROCESSING_PATHNAME,
  RELEASED_PROCESSING_SURFACE,
  RELEASED_PROCESSING_CUT_SHEET_SURFACE,
  RELEASED_EQUIPMENT_ADMIN_RECORD_SURFACE,
  RELEASED_PRODUCTION_PATHNAME,
  RELEASED_PRODUCTION_SURFACE,
  RELEASED_PROJECTS_PATHNAME,
  RELEASED_PROJECTS_SURFACE,
  RELEASED_SHEEP_BATCHES_DETAIL_SURFACE,
  RELEASED_SHEEP_BATCHES_INDEX_PATHNAME,
  RELEASED_SHEEP_BATCHES_INDEX_SURFACE,
  RELEASED_SHEEP_FLOCKS_DETAIL_SURFACE,
  RELEASED_SHEEP_FLOCKS_INDEX_PATHNAME,
  RELEASED_SHEEP_FLOCKS_INDEX_SURFACE,
  RELEASED_SHEEP_DAILYS_DETAIL_SURFACE,
  RELEASED_SHEEP_DAILYS_INDEX_PATHNAME,
  RELEASED_SHEEP_DAILYS_INDEX_SURFACE,
  RELEASED_EGG_DAILYS_DETAIL_SURFACE,
  RELEASED_EGG_DAILYS_INDEX_PATHNAME,
  RELEASED_EGG_DAILYS_INDEX_SURFACE,
  RELEASED_PIG_DAILYS_DETAIL_SURFACE,
  RELEASED_PIG_DAILYS_INDEX_PATHNAME,
  RELEASED_PIG_DAILYS_INDEX_SURFACE,
  RELEASED_BROILER_DAILYS_DETAIL_SURFACE,
  RELEASED_BROILER_DAILYS_INDEX_PATHNAME,
  RELEASED_BROILER_DAILYS_INDEX_SURFACE,
  RELEASED_SHEEP_HOME_PATHNAME,
  RELEASED_SHEEP_HOME_SURFACE,
  RELEASED_SHEEP_WEIGHINS_PATHNAME,
  RELEASED_SHEEP_WEIGHINS_SURFACE,
  RELEASED_TASKS_CENTER_SURFACE,
  RELEASED_TASKS_INSTANCE_DETAIL_SURFACE,
  RELEASED_TASKS_TODO_DETAIL_SURFACE,
  RELEASED_WEATHER_PATHNAME,
  RELEASED_WEATHER_SURFACE,
  RELEASED_WEIGHINS_WEBFORM_PATHNAME,
  RELEASED_WEIGHINS_WEBFORM_SURFACE,
  RELEASED_WEIGHIN_SESSION_RECORD_SURFACE,
  isReleasedCattleBatchesDetailPathname,
  isReleasedCattleDailysDetailPathname,
  isReleasedCattleForecastBatchDetailPathname,
  isReleasedEquipmentChecklistEntryPathname,
  isReleasedEquipmentFleetDetailPathname,
  isReleasedCattleHerdsDetailPathname,
  isReleasedEquipmentFleetIndexPathname,
  isReleasedEquipmentFuelingEntryPathname,
  isReleasedEquipmentFuelingFormPathname,
  isReleasedSheepBatchesDetailPathname,
  isReleasedSheepFlocksDetailPathname,
  isReleasedLayerDailysDetailPathname,
  isReleasedLayerBatchesDetailPathname,
  isReleasedLayerHousingsDetailPathname,
  isReleasedSheepDailysDetailPathname,
  isReleasedEggDailysDetailPathname,
  isReleasedPigDailysDetailPathname,
  isReleasedPigBatchesDetailPathname,
  isReleasedBroilerDailysDetailPathname,
  isReleasedBroilerBatchesDetailPathname,
  isReleasedBroilerBatchFormFullscreenHostPathname,
  isReleasedTasksCenterPathname,
  isReleasedTasksInstanceDetailPathname,
  isReleasedTasksTodoDetailPathname,
  isReleasedPigSowsPathname,
  isReleasedProcessingCutSheetPathname,
  isReleasedEquipmentAdminRecordPathname,
  isReleasedWebformHubDailysPathname,
  isReleasedWeighInSessionRecordPathname,
} from './releasedSurfaceBoundary.ts';
import type {ReleasedSurfaceClaim} from './releasedSurfaceBoundary.ts';

// Pasture Map was the first component in this file that could not be imported
// eagerly; it
// pulls leaflet + leaflet-geoman + turf + togeojson — the single largest deep
// dependency cluster in the app — and the Pasture Map performance boundary
// route-splits it behind React.lazy for exactly that reason. This module is
// imported by App at module
// scope, so a static import here would put that cluster back in the entry
// chunk for every user on every route and silently undo the pilot.
// tests/static/pasture_map_lazy_route_static.test.js holds both halves: no
// eager importer anywhere in src/, and this site declared lazily.
const PastureMapView = React.lazy(() => import('../../pasture/PastureMapView.tsx'));

// These released route leaves are imported by App through this renderer. Keep
// them at a route boundary so an unrelated released surface does not pull them
// into the startup chunk. HomeDashboard and LightHomePortal deliberately stay
// eager: App renders them outside this renderer for its home/light containment
// paths as well.
const ActivityLogView = React.lazy(() => import('../../activity/ActivityLogView.tsx'));
const ProjectsRoute = React.lazy(() => import('../../projects/ProjectsRoute.tsx'));
const ClientErrorsView = React.lazy(() => import('../../admin/ClientErrorsView.tsx'));
const WebformsAdminView = React.lazy(() => import('../../webforms/WebformsAdminView.tsx'));
const AnimalHistoryPage = React.lazy(() => import('../../dashboard/AnimalHistoryPage.tsx'));
const MySubmissions = React.lazy(() => import('../../dashboard/MySubmissions.tsx'));
const ProductionPage = React.lazy(() => import('../../dashboard/ProductionPage.tsx'));
const EquipmentHome = React.lazy(() => import('../../equipment/EquipmentHome.tsx'));
const LivestockWeighInsView = React.lazy(() => import('../../livestock/LivestockWeighInsView.tsx'));
const WeighInSessionPage = React.lazy(() => import('../../livestock/WeighInSessionPage.tsx'));
const NewsletterAdminView = React.lazy(() => import('../../newsletter/NewsletterAdminView.tsx'));
const ProcessingCalendarView = React.lazy(() => import('../../processing/ProcessingCalendarView.tsx'));
// Lazy for the same reason its Calendar sibling is, and more so: the editor
// pulls a species catalog, a template field map and the PDF renderer, and each
// catalog is itself a dynamic import inside the page. A static import here
// would put all of that in the startup chunk for every user on every route.
const ProcessingCutSheetPage = React.lazy(() => import('../../processing/ProcessingCutSheetPage.tsx'));
const EquipmentAdminRecordPage = React.lazy(() => import('../../admin/EquipmentAdminRecordPage.tsx'));
const TaskCenterView = React.lazy(() => import('../../tasks/TaskCenterView.tsx'));
const WeatherPage = React.lazy(() => import('../../weather/WeatherPage.tsx'));
// /pig/feed: the feed ledger and its two confirmed autosaves are route-local,
// so they load with the route instead of in the startup chunk (the bundle
// budget measured the eager import at 685 B over the initial-js gzip limit).
const PigFeedView = React.lazy(() => import('../../pig/PigFeedView.tsx'));

function RouteLoadingFallback() {
  return React.createElement(
    'div',
    {role: 'status', 'aria-live': 'polite', style: {padding: '2rem', textAlign: 'center'}},
    'Loading route...',
  );
}

// Broiler batch actions are still App closures: they own the save-time merge,
// maintenance publish, and form state. The released renderer deliberately does
// not close over App, so App provides this narrow bridge only around a verified
// released render. Missing actions fail closed to null instead of producing a
// read-only-looking form whose writes disappear.
const ReleasedBroilerBatchesActionsContext = React.createContext<BroilerListProps | null>(null);

export function ReleasedBroilerBatchesActionsProvider({
  actions,
  children,
}: {
  actions: BroilerListProps;
  children?: React.ReactNode;
}) {
  return (
    <ReleasedBroilerBatchesActionsContext.Provider value={actions}>
      {children}
    </ReleasedBroilerBatchesActionsContext.Provider>
  );
}

function ReleasedBroilerBatchesMount() {
  const actions = React.useContext(ReleasedBroilerBatchesActionsContext);
  if (!actions) return null;
  return React.createElement(BroilerListRouter, actions);
}

function ReleasedBroilerTimelineMount() {
  const actions = React.useContext(ReleasedBroilerBatchesActionsContext);
  if (!actions) return null;
  return React.createElement(BroilerTimelineView, {
    openAdd: actions.openAdd,
    upd: actions.upd,
    closeForm: actions.closeForm,
    submit: actions.submit,
    del: actions.del,
    parseProcessorXlsx: actions.parseProcessorXlsx,
    persistDocuments: actions.persistDocuments,
  });
}

// BatchForm's submit/delete/document operations remain App closures. The
// full-screen release receives that exact, deliberately small set only after
// App has verified the current capability. The action-state mount below loads
// their three cold inputs and commits them through the existing context setters
// before it attaches the form, so a released edit cannot preserve the callback
// identity while losing its changed row to an empty App array.
const ReleasedBroilerBatchFormActionsContext = React.createContext<BatchFormFullscreenActions | null>(null);
const BROILER_BATCH_FORM_ACTION_STATE_PARAMS = Object.freeze({});

export function ReleasedBroilerBatchFormActionsProvider({
  actions,
  children,
}: {
  actions: BatchFormFullscreenActions;
  children?: React.ReactNode;
}) {
  return (
    <ReleasedBroilerBatchFormActionsContext.Provider value={actions}>
      {children}
    </ReleasedBroilerBatchFormActionsContext.Provider>
  );
}

function ReleasedBroilerBatchFormFullscreenMount() {
  const actions = React.useContext(ReleasedBroilerBatchFormActionsContext);
  const {setBatches} = useBatches();
  const {setFeedCosts} = useFeedCosts();
  const {setLayerBatches} = useLayer();
  const actionStateResource = useRouteResource(
    RESOURCE.BROILER_BATCH_FORM_ACTION_STATE,
    BROILER_BATCH_FORM_ACTION_STATE_PARAMS,
  );
  const actionState = actionStateResource.data as BroilerBatchFormActionStateData | null;
  // There must be one committed context turn between loading the route-local
  // base and attaching App's closures. The first layout effect publishes the
  // base; App then rebuilds `upd`/`submit`/`del` over it; the second effect
  // permits BatchForm only with that rebuilt provider value. Layout effects run
  // before paint, so no stale callback can become interactive in-between.
  const [synchronizedState, setSynchronizedState] = React.useState<BroilerBatchFormActionStateData | null>(null);
  const [actionsReady, setActionsReady] = React.useState(false);
  React.useLayoutEffect(() => {
    setActionsReady(false);
    if (!actionState) {
      setSynchronizedState(null);
      return;
    }
    setBatches(actionState.batches);
    setFeedCosts(actionState.feedCosts);
    setLayerBatches(actionState.layerBatches);
    setSynchronizedState(actionState);
  }, [actionState, setBatches, setFeedCosts, setLayerBatches]);
  React.useLayoutEffect(() => {
    if (synchronizedState !== actionState || !actionState) return;
    setActionsReady(true);
  }, [actionState, actions, synchronizedState]);

  if (!actions) return null;
  if (!actionState && actionStateResource.error) {
    return (
      <div data-broiler-batch-form-action-state-error="true" style={{minHeight: '100vh', padding: 24}}>
        <div style={{maxWidth: 560, margin: '12vh auto', color: 'var(--ink)'}}>
          <h1 style={{fontSize: 20, marginBottom: 8}}>Batch editor unavailable</h1>
          <p style={{marginBottom: 14}}>Could not prepare the current batch data. Retry before saving changes.</p>
          <button type="button" data-broiler-batch-form-action-state-retry="true" onClick={actionStateResource.refetch}>
            Retry
          </button>
        </div>
      </div>
    );
  }
  if (!actionState || !actionsReady) {
    return (
      <div data-broiler-batch-form-action-state-loading="true" style={{minHeight: '100vh', padding: 24}}>
        <div style={{maxWidth: 560, margin: '12vh auto', color: 'var(--ink)'}}>Preparing batch editor…</div>
      </div>
    );
  }
  return React.createElement(BatchForm, {
    ...actions,
    timelinePreview: React.createElement(BroilerTimelineView, {embedded: true}),
  });
}

export function renderReleasedAuthenticatedSurface(claim: ReleasedSurfaceClaim | null | undefined) {
  if (!claim) return null;

  if (
    claim.resolution.surface === RELEASED_LIGHT_HOME_SURFACE &&
    claim.resolution.routedPathname === RELEASED_LIGHT_HOME_PATHNAME
  ) {
    return React.createElement(LightHomePortal);
  }

  if (
    claim.resolution.surface === RELEASED_STANDARD_HOME_SURFACE &&
    claim.resolution.routedPathname === RELEASED_STANDARD_HOME_PATHNAME
  ) {
    return React.createElement(HomeDashboard);
  }

  if (
    claim.resolution.surface === RELEASED_CATTLE_BREEDING_SURFACE &&
    claim.resolution.routedPathname === RELEASED_CATTLE_BREEDING_PATHNAME
  ) {
    return React.createElement(CattleBreedingView);
  }

  if (
    claim.resolution.surface === RELEASED_CATTLE_BATCHES_DETAIL_SURFACE &&
    isReleasedCattleBatchesDetailPathname(claim.resolution.routedPathname)
  ) {
    // The router retains its forecast and historic-suffix fallback, while the
    // boundary above admits only the exact persisted-record target.
    return React.createElement(CattleBatchesRouter);
  }

  if (
    claim.resolution.surface === RELEASED_CATTLE_BATCHES_INDEX_SURFACE &&
    claim.resolution.routedPathname === RELEASED_CATTLE_BATCHES_INDEX_PATHNAME
  ) {
    // One router serves all three `/cattle/batches*` surfaces. The boundary has
    // already proved this claim is the bare canonical hub path, for which the
    // router's record and forecast branches cannot match, so it lands on
    // CattleBatchesHub without a second mount path being introduced.
    return React.createElement(CattleBatchesRouter);
  }

  if (
    claim.resolution.surface === RELEASED_BROILER_BATCHES_DETAIL_SURFACE &&
    isReleasedBroilerBatchesDetailPathname(claim.resolution.routedPathname)
  ) {
    return React.createElement(ReleasedBroilerBatchesMount);
  }

  if (
    claim.resolution.surface === RELEASED_BROILER_BATCHES_INDEX_SURFACE &&
    claim.resolution.routedPathname === RELEASED_BROILER_BATCHES_INDEX_PATHNAME
  ) {
    return React.createElement(ReleasedBroilerBatchesMount);
  }

  if (
    claim.resolution.surface === RELEASED_BROILER_BATCH_FORM_FULLSCREEN_SURFACE &&
    isReleasedBroilerBatchFormFullscreenHostPathname(claim.resolution.routedPathname)
  ) {
    return React.createElement(ReleasedBroilerBatchFormFullscreenMount);
  }

  if (
    claim.resolution.surface === RELEASED_CATTLE_FORECAST_BATCH_DETAIL_SURFACE &&
    isReleasedCattleForecastBatchDetailPathname(claim.resolution.routedPathname)
  ) {
    // The same router keeps the persisted-record, forecast projection, and
    // legacy hub branches in their historical order. The boundary above admits
    // only the strict forecast month target for this renderer branch.
    return React.createElement(CattleBatchesRouter);
  }

  if (
    claim.resolution.surface === RELEASED_CATTLE_FORECAST_SURFACE &&
    claim.resolution.routedPathname === RELEASED_CATTLE_FORECAST_PATHNAME
  ) {
    return React.createElement(CattleForecastView);
  }

  if (
    claim.resolution.surface === RELEASED_CATTLE_HERDS_INDEX_SURFACE &&
    claim.resolution.routedPathname === RELEASED_CATTLE_HERDS_INDEX_PATHNAME
  ) {
    // The hub presenter directly, not its router: the router's only job is the
    // suffix split this boundary has already decided.
    return React.createElement(CattleHerdsHub);
  }

  if (
    claim.resolution.surface === RELEASED_CATTLE_HERDS_DETAIL_SURFACE &&
    isReleasedCattleHerdsDetailPathname(claim.resolution.routedPathname)
  ) {
    // The record half goes through the router, which owns the sb/fmt/authState
    // props CattleAnimalPage is mounted with today. Reproducing that wiring
    // here would be a second, divergent mount contract for one page.
    return React.createElement(CattleHerdsRouter);
  }

  if (
    claim.resolution.surface === RELEASED_CATTLE_DAILYS_INDEX_SURFACE &&
    claim.resolution.routedPathname === RELEASED_CATTLE_DAILYS_INDEX_PATHNAME
  ) {
    return React.createElement(CattleDailysRouter);
  }

  if (
    claim.resolution.surface === RELEASED_MY_SUBMISSIONS_SURFACE &&
    claim.resolution.routedPathname === RELEASED_MY_SUBMISSIONS_PATHNAME
  ) {
    return React.createElement(
      React.Suspense,
      {fallback: React.createElement(RouteLoadingFallback)},
      React.createElement(MySubmissions),
    );
  }

  if (
    claim.resolution.surface === RELEASED_FUEL_SUPPLY_SURFACE &&
    claim.resolution.routedPathname === RELEASED_FUEL_SUPPLY_PATHNAME
  ) {
    return React.createElement(FuelingAuthenticatedBridge, null, React.createElement(FuelSupplyWebform));
  }

  if (
    claim.resolution.surface === RELEASED_EQUIPMENT_FUELING_SUPPLY_SURFACE &&
    claim.resolution.routedPathname === RELEASED_EQUIPMENT_FUELING_SUPPLY_PATHNAME
  ) {
    return React.createElement(FuelingAuthenticatedBridge, null, React.createElement(FuelingHub));
  }

  if (
    claim.resolution.surface === RELEASED_EQUIPMENT_FUELING_HUB_SURFACE &&
    claim.resolution.routedPathname === RELEASED_EQUIPMENT_FUELING_HUB_PATHNAME
  ) {
    return React.createElement(FuelingAuthenticatedBridge, null, React.createElement(FuelingHub));
  }

  if (
    claim.resolution.surface === RELEASED_EQUIPMENT_FLEET_INDEX_SURFACE &&
    isReleasedEquipmentFleetIndexPathname(claim.resolution.routedPathname)
  ) {
    return React.createElement(
      React.Suspense,
      {fallback: React.createElement(RouteLoadingFallback)},
      React.createElement(EquipmentHome),
    );
  }

  if (
    claim.resolution.surface === RELEASED_EQUIPMENT_FUELING_FORM_SURFACE &&
    isReleasedEquipmentFuelingFormPathname(claim.resolution.routedPathname)
  ) {
    // FuelingHub owns the form's core equipment projection and passes the
    // selected machine into EquipmentFuelingWebform. Keep the same bridge and
    // container rather than creating a second direct form path.
    return React.createElement(FuelingAuthenticatedBridge, null, React.createElement(FuelingHub));
  }

  if (
    claim.resolution.surface === RELEASED_ACTIVITY_SURFACE &&
    claim.resolution.routedPathname === RELEASED_ACTIVITY_PATHNAME
  ) {
    return React.createElement(
      UnauthorizedRedirect,
      {requireAdmin: false, fallbackView: 'home'},
      React.createElement(
        React.Suspense,
        {fallback: React.createElement(RouteLoadingFallback)},
        React.createElement(ActivityLogView),
      ),
    );
  }

  // Projects: the same signed-out gate and Suspense boundary AppEntry wraps
  // around the lazy route; every active role may open the page (D12).
  if (
    claim.resolution.surface === RELEASED_PROJECTS_SURFACE &&
    claim.resolution.routedPathname === RELEASED_PROJECTS_PATHNAME
  ) {
    return React.createElement(
      UnauthorizedRedirect,
      {requireAdmin: false, fallbackView: 'home'},
      React.createElement(
        React.Suspense,
        {fallback: React.createElement(RouteLoadingFallback)},
        React.createElement(ProjectsRoute),
      ),
    );
  }

  if (
    claim.resolution.surface === RELEASED_ADD_FEED_SURFACE &&
    claim.resolution.routedPathname === RELEASED_ADD_FEED_PATHNAME
  ) {
    return React.createElement(AddFeedAuthenticatedBridge, null, React.createElement(AddFeedWebform));
  }

  // Add Feed's sibling, with one difference that is the whole reason this
  // branch names a MOUNT instead of the bridge. AddFeedWebform takes no
  // identity prop, while PigDailysWebform takes `sessionSubmitter` — which App
  // supplies through the webforms route entry on the legacy path, and which no
  // App is running to supply here. (Naming that entry component in this file,
  // even in a comment, is refused by the wave-two and wave-three guards: this
  // renderer must never look like it delegates to a domain entry.)
  // PigDailysWebformMount is that one derivation plus this exact bridge/child
  // pair, and it lives beside the bridge so the two cannot drift apart.
  if (
    claim.resolution.surface === RELEASED_PIG_DAILYS_WEBFORM_SURFACE &&
    claim.resolution.routedPathname === RELEASED_PIG_DAILYS_WEBFORM_PATHNAME
  ) {
    return React.createElement(PigDailysWebformMount);
  }

  // The hub's release is a fixed whitelist inside a splat, not a generic
  // `/dailys/*` renderer. The boundary has already checked the raw pair; keep
  // the predicate here too so a future target cannot widen this mount by only
  // changing the resolver. The authenticated mount recreates the three former
  // App props from AuthContext before handing the auth-free presenter to its
  // existing resource bridge.
  if (
    claim.resolution.surface === RELEASED_WEBFORM_HUB_SURFACE &&
    isReleasedWebformHubDailysPathname(claim.resolution.routedPathname)
  ) {
    return React.createElement(WebformHubAuthenticatedMount);
  }

  if (
    claim.resolution.surface === RELEASED_NEWSLETTER_ADMIN_SURFACE &&
    claim.resolution.routedPathname === RELEASED_NEWSLETTER_ADMIN_PATHNAME
  ) {
    return React.createElement(
      UnauthorizedRedirect,
      {requireAdmin: true, fallbackView: 'home'},
      React.createElement(
        React.Suspense,
        {fallback: React.createElement(RouteLoadingFallback)},
        React.createElement(NewsletterAdminView),
      ),
    );
  }

  // The admin gate is the WHOLE admin gate. `resolveSurfaceAccess` denies only
  // `light` at this route — management, farm_team, equipment_tech and inactive
  // all resolve RELEASED once the ledger names the surface, exactly as they
  // already do for the released Newsletter admin above. Admin-only enforcement
  // therefore rests entirely on this wrapper, so it is carried verbatim from
  // AppEntry rather than restated.
  if (
    claim.resolution.surface === RELEASED_CLIENT_ERRORS_SURFACE &&
    claim.resolution.routedPathname === RELEASED_CLIENT_ERRORS_PATHNAME
  ) {
    return React.createElement(
      UnauthorizedRedirect,
      {requireAdmin: true, fallbackView: 'home'},
      React.createElement(
        React.Suspense,
        {fallback: React.createElement(RouteLoadingFallback)},
        React.createElement(ClientErrorsView),
      ),
    );
  }

  // This route owns its own authenticated reads, writes, recovery refresh and
  // admin-tab state.  The existing redirect remains the authority for the
  // stricter admin gate, just as it does for the two sibling /admin routes.
  if (
    claim.resolution.surface === RELEASED_WEBFORMS_ADMIN_SURFACE &&
    claim.resolution.routedPathname === RELEASED_WEBFORMS_ADMIN_PATHNAME
  ) {
    return React.createElement(
      UnauthorizedRedirect,
      {requireAdmin: true, fallbackView: 'home'},
      React.createElement(
        React.Suspense,
        {fallback: React.createElement(RouteLoadingFallback)},
        React.createElement(WebformsAdminView),
      ),
    );
  }

  // A40: the Admin equipment record page. A PREFIX predicate like the cut-sheet
  // branch, because the slug is part of the address; the admin wrapper is the
  // whole admin gate here exactly as for the three /admin siblings above, and
  // is carried verbatim from AppEntry.
  if (
    claim.resolution.surface === RELEASED_EQUIPMENT_ADMIN_RECORD_SURFACE &&
    isReleasedEquipmentAdminRecordPathname(claim.resolution.routedPathname)
  ) {
    return React.createElement(
      UnauthorizedRedirect,
      {requireAdmin: true, fallbackView: 'home'},
      React.createElement(
        React.Suspense,
        {fallback: React.createElement(RouteLoadingFallback)},
        React.createElement(EquipmentAdminRecordPage),
      ),
    );
  }

  if (
    claim.resolution.surface === RELEASED_CATTLE_LOG_SURFACE &&
    claim.resolution.routedPathname === RELEASED_CATTLE_LOG_PATHNAME
  ) {
    return React.createElement(CattleLogPage);
  }

  // Pasture Map takes NO props on the legacy path either — the app's own route
  // hands PastureMapView nothing, it renders its own Header, and every read it
  // makes goes through the four `pasture.*` catalog resources while every write
  // goes through the pastureMapApi SECDEF wrappers on their own client. So the
  // only thing this branch has to reproduce is the gate/Suspense NESTING: the
  // Suspense boundary sits INSIDE UnauthorizedRedirect so an unauthorized or
  // still-loading caller never triggers the dynamic import fetch at all. The
  // fallback is a live region, as the route's own is, because a slow chunk on a
  // field connection is the common case here rather than the rare one.
  //
  // `requireAdmin: false` is the whole gate and it is deliberate: /pasture-map
  // is reachable by every authenticated role INCLUDING light (it is in
  // LIGHT_ALLOWED_VIEWS), so the resolver's access check denies nobody here and
  // this wrapper's only job is the signed-out case.
  if (
    claim.resolution.surface === RELEASED_PASTURE_MAP_SURFACE &&
    claim.resolution.routedPathname === RELEASED_PASTURE_MAP_PATHNAME
  ) {
    return React.createElement(
      UnauthorizedRedirect,
      {requireAdmin: false, fallbackView: 'home'},
      React.createElement(
        React.Suspense,
        {
          fallback: React.createElement(
            'div',
            {role: 'status', 'aria-live': 'polite', style: {padding: '2rem', textAlign: 'center'}},
            'Loading Pasture Map…',
          ),
        },
        React.createElement(PastureMapView),
      ),
    );
  }

  if (
    claim.resolution.surface === RELEASED_PROCESSING_SURFACE &&
    claim.resolution.routedPathname === RELEASED_PROCESSING_PATHNAME
  ) {
    return React.createElement(
      UnauthorizedRedirect,
      {requireAdmin: false, fallbackView: 'home'},
      React.createElement(
        React.Suspense,
        {fallback: React.createElement(RouteLoadingFallback)},
        React.createElement(ProcessingCalendarView),
      ),
    );
  }

  // The page directly, not a router, and with ZERO props — AppEntry mounts
  // `<ProcessingCutSheetPage />` bare inside the same `requireAdmin: false`
  // wrapper, and the page derives its record id from `useLocation()` itself.
  // The wrapper is carried over verbatim because it is the signed-out gate, and
  // this mount must not be stricter or looser than the legacy one.
  //
  // What it is NOT is the processing-role gate. `resolveSurfaceAccess` bounces
  // on the literal `view === 'processing'` (src/lib/access.ts), so the role
  // bounce that protects the Calendar does not cover this view; Light
  // containment does cover it, since `processingCutSheet` is absent from
  // LIGHT_ALLOWED_VIEWS. Widening that literal is a change to an access file
  // this module does not own, and the server RPCs behind the editor remain the
  // enforcement of record, so nothing is asserted here that is not true.
  //
  // A PREFIX predicate, like the weigh-in session record branch below and unlike
  // the exact-pathname siblings around it, because the record id is part of the
  // address. The boundary has already proved this pathname carries exactly one
  // decoded-safe segment and at most the two carried UI facts (`?tab=diagram`
  // and/or `#usual`; anything else refuses), so the page's own
  // `cutSheetEditorRecordId` cannot be handed the splat base or a canonicalized
  // transition frame here — it re-derives from the same fence and would return
  // null, which the page renders as its missing-record state.
  if (
    claim.resolution.surface === RELEASED_PROCESSING_CUT_SHEET_SURFACE &&
    isReleasedProcessingCutSheetPathname(claim.resolution.routedPathname)
  ) {
    return React.createElement(
      UnauthorizedRedirect,
      {requireAdmin: false, fallbackView: 'home'},
      React.createElement(
        React.Suspense,
        {fallback: React.createElement(RouteLoadingFallback)},
        React.createElement(ProcessingCutSheetPage),
      ),
    );
  }

  if (
    claim.resolution.surface === RELEASED_PRODUCTION_SURFACE &&
    claim.resolution.routedPathname === RELEASED_PRODUCTION_PATHNAME
  ) {
    return React.createElement(
      React.Suspense,
      {fallback: React.createElement(RouteLoadingFallback)},
      React.createElement(ProductionPage),
    );
  }

  if (
    claim.resolution.surface === RELEASED_CATTLE_HOME_SURFACE &&
    claim.resolution.routedPathname === RELEASED_CATTLE_HOME_PATHNAME
  ) {
    return React.createElement(CattleHomeView);
  }

  if (
    claim.resolution.surface === RELEASED_SHEEP_HOME_SURFACE &&
    claim.resolution.routedPathname === RELEASED_SHEEP_HOME_PATHNAME
  ) {
    return React.createElement(SheepHomeView);
  }

  // The remaining three program dashboards mount exactly as Cattle and Sheep
  // do: their domain entries hand them NO props (BroilerEntry.tsx,
  // LayerEntry.tsx and PigEntry.tsx each mount `<X />`), each renders its own
  // Header, and program access plus Light containment are already decided by
  // resolveSurfaceAccess inside the resolver — so no UnauthorizedRedirect
  // wrapper is added here either.
  if (
    claim.resolution.surface === RELEASED_BROILER_HOME_SURFACE &&
    claim.resolution.routedPathname === RELEASED_BROILER_HOME_PATHNAME
  ) {
    return React.createElement(BroilerHomeView);
  }

  if (
    claim.resolution.surface === RELEASED_LAYERS_HOME_SURFACE &&
    claim.resolution.routedPathname === RELEASED_LAYERS_HOME_PATHNAME
  ) {
    return React.createElement(LayersHomeView);
  }

  // LayersView now owns both its route resource and its app_store write through
  // the shared transport seam, so the released mount needs no LayerEntry or App
  // callback. The context setter it uses is an optimistic publish only; it does
  // not make the rendered route depend on legacy boot data.
  if (
    claim.resolution.surface === RELEASED_LAYER_GROUPS_SURFACE &&
    claim.resolution.routedPathname === RELEASED_LAYER_GROUPS_PATHNAME
  ) {
    return React.createElement(LayersView);
  }

  if (
    claim.resolution.surface === RELEASED_PIGS_HOME_SURFACE &&
    claim.resolution.routedPathname === RELEASED_PIGS_HOME_PATHNAME
  ) {
    return React.createElement(PigsHomeView);
  }

  if (
    claim.resolution.surface === RELEASED_SHEEP_DAILYS_INDEX_SURFACE &&
    claim.resolution.routedPathname === RELEASED_SHEEP_DAILYS_INDEX_PATHNAME
  ) {
    // Exactly `/sheep/dailys`, so the router's `dailyId` is null and it renders
    // SheepDailysHub. The splat base `/sheep/dailys/` never reaches here. This
    // is the SAME element the record branch below mounts, and the same one
    // SheepEntry renders at `/sheep/dailys/*` — SheepDailysView's default
    // export, bare. Mounting the hub directly is not an option anyway: it is
    // module-private.
    return React.createElement(SheepDailysRouter);
  }

  if (
    claim.resolution.surface === RELEASED_SHEEP_DAILYS_DETAIL_SURFACE &&
    isReleasedSheepDailysDetailPathname(claim.resolution.routedPathname)
  ) {
    // The router keeps its hub fallback for every other suffix; the boundary
    // above admits only the exact persisted daily-report target.
    return React.createElement(SheepDailysRouter);
  }

  if (
    claim.resolution.surface === RELEASED_WEATHER_SURFACE &&
    claim.resolution.routedPathname === RELEASED_WEATHER_PATHNAME
  ) {
    return React.createElement(
      React.Suspense,
      {fallback: React.createElement(RouteLoadingFallback)},
      React.createElement(WeatherPage),
    );
  }

  if (
    claim.resolution.surface === RELEASED_LAYER_BATCHES_INDEX_SURFACE &&
    claim.resolution.routedPathname === RELEASED_LAYER_BATCHES_INDEX_PATHNAME
  ) {
    return React.createElement(LayerBatchesHub);
  }

  // The two layer record pages are the released surfaces here that mount
  // NEITHER their router nor their page. Neither page can be mounted directly:
  // both take `authState` as a prop and this is a plain function, not a
  // component, so there is no `useAuth` to call. LayerBatchesRouter cannot stand
  // in for them either — the two release fences deliberately refuse wider raw
  // shapes for App's explicit recovery disposition, so routing an audited shape
  // through it would erase that distinction.
  //
  // `LayerHousingRecordMount` and `LayerBatchRecordMount` are that one hook plus
  // the same three props each router branch passes, and they live beside those
  // branches so the pairs cannot drift apart unnoticed.
  if (
    claim.resolution.surface === RELEASED_LAYER_HOUSINGS_DETAIL_SURFACE &&
    isReleasedLayerHousingsDetailPathname(claim.resolution.routedPathname)
  ) {
    return React.createElement(LayerHousingRecordMount);
  }

  if (
    claim.resolution.surface === RELEASED_LAYER_BATCHES_DETAIL_SURFACE &&
    isReleasedLayerBatchesDetailPathname(claim.resolution.routedPathname)
  ) {
    return React.createElement(LayerBatchRecordMount);
  }

  // BOTH Layer Dailys branches mount the ROUTER, not a page — unlike the Layer
  // Batches release just above, which mounts its hub directly. `LayerDailysHub`
  // is module-private, and the router is where the shared
  // `layer.dailys.groupDirectory` read lives, above the index/record branch.
  // Mounting either half directly would drop that read and create a second data
  // path for the group dropdown. It is also the same element LayerEntry renders
  // at `/layer/dailys/*`: LayerDailysView's default export, with no route guard
  // around it, because the layer entry mounts it bare.
  if (
    claim.resolution.surface === RELEASED_LAYER_DAILYS_INDEX_SURFACE &&
    claim.resolution.routedPathname === RELEASED_LAYER_DAILYS_INDEX_PATHNAME
  ) {
    // Exactly `/layer/dailys`, so the router's `dailyId` is null and it renders
    // the hub. The splat base `/layer/dailys/` never reaches here.
    return React.createElement(LayerDailysRouter);
  }

  if (
    claim.resolution.surface === RELEASED_LAYER_DAILYS_DETAIL_SURFACE &&
    isReleasedLayerDailysDetailPathname(claim.resolution.routedPathname)
  ) {
    // The boundary above has already proved this is the exact one-segment
    // record form, so the router's own branch order lands on LayerDailyPage;
    // its hub branch keeps its existing behaviour for every other path.
    return React.createElement(LayerDailysRouter);
  }

  // The other three daily routers, on exactly the terms the Layer Dailys pair
  // above sets. Each mounts the ROUTER, never a half: every hub here is
  // module-private, and each router performs the shared directory/projection
  // read ABOVE its own index/record branch, so mounting a half directly would
  // drop that read and open a second data path for one dropdown. Each is also
  // the same element its domain entry renders at the splat — the view module's
  // default export, bare, with no route guard around it.
  if (
    claim.resolution.surface === RELEASED_EGG_DAILYS_INDEX_SURFACE &&
    claim.resolution.routedPathname === RELEASED_EGG_DAILYS_INDEX_PATHNAME
  ) {
    // Exactly `/layer/eggs`, so the router's `dailyId` is null and it renders
    // EggDailysHub. The splat base `/layer/eggs/` never reaches here.
    return React.createElement(EggDailysRouter);
  }

  if (
    claim.resolution.surface === RELEASED_EGG_DAILYS_DETAIL_SURFACE &&
    isReleasedEggDailysDetailPathname(claim.resolution.routedPathname)
  ) {
    return React.createElement(EggDailysRouter);
  }

  if (
    claim.resolution.surface === RELEASED_PIG_DAILYS_INDEX_SURFACE &&
    claim.resolution.routedPathname === RELEASED_PIG_DAILYS_INDEX_PATHNAME
  ) {
    return React.createElement(PigDailysRouter);
  }

  if (
    claim.resolution.surface === RELEASED_PIG_DAILYS_DETAIL_SURFACE &&
    isReleasedPigDailysDetailPathname(claim.resolution.routedPathname)
  ) {
    return React.createElement(PigDailysRouter);
  }

  // PigBatchesView is the shared splat presenter for the hub and record
  // workspace. The release boundary has partitioned its permissive internal
  // pathname handling into the exact hub and one-safe-id record contracts, so
  // this remains the one existing mount rather than introducing a second page
  // contract for either half.
  if (
    claim.resolution.surface === RELEASED_PIG_BATCHES_INDEX_SURFACE &&
    claim.resolution.routedPathname === RELEASED_PIG_BATCHES_INDEX_PATHNAME
  ) {
    return React.createElement(PigBatchesView);
  }

  if (
    claim.resolution.surface === RELEASED_PIG_BATCHES_DETAIL_SURFACE &&
    isReleasedPigBatchesDetailPathname(claim.resolution.routedPathname)
  ) {
    return React.createElement(PigBatchesView);
  }

  // BroilerDailysRouter is the one router in this family that takes a prop:
  // BroilerEntry passes `refreshDailys`, App's poultry fan-out into the
  // DailysRecentContext mirror. The released mount deliberately passes none,
  // and the hub's four call sites are already written `refreshDailys &&
  // refreshDailys('broiler')`. That fan-out is redundant once this surface is
  // released: route resources own the data and a fenced raw pathname never
  // reaches this renderer. App instead selects URL synchronization, a reviewed
  // redirect, or terminal recovery. Nothing on this surface reads that mirror,
  // and the ordinary router mount keeps the prop unchanged for its canonical
  // branch.
  if (
    claim.resolution.surface === RELEASED_BROILER_DAILYS_INDEX_SURFACE &&
    claim.resolution.routedPathname === RELEASED_BROILER_DAILYS_INDEX_PATHNAME
  ) {
    return React.createElement(BroilerDailysRouter);
  }

  if (
    claim.resolution.surface === RELEASED_BROILER_DAILYS_DETAIL_SURFACE &&
    isReleasedBroilerDailysDetailPathname(claim.resolution.routedPathname)
  ) {
    // The record half never received `refreshDailys` even under legacy boot:
    // BroilerDailysRouter hands PoultryDailyPage only {sb, fmt, authState,
    // batches}, so this branch is prop-identical to the legacy render.
    return React.createElement(BroilerDailysRouter);
  }

  if (
    claim.resolution.surface === RELEASED_EQUIPMENT_FLEET_DETAIL_SURFACE &&
    isReleasedEquipmentFleetDetailPathname(claim.resolution.routedPathname)
  ) {
    // EquipmentHome owns its exact detail renderer and its separate entry
    // namespaces. The boundary has already proved this is only the audited
    // one-segment detail form; every other /fleet/* path is handled by explicit
    // route recovery rather than this renderer.
    return React.createElement(
      React.Suspense,
      {fallback: React.createElement(RouteLoadingFallback)},
      React.createElement(EquipmentHome),
    );
  }

  if (
    claim.resolution.surface === RELEASED_EQUIPMENT_FUELING_ENTRY_SURFACE &&
    isReleasedEquipmentFuelingEntryPathname(claim.resolution.routedPathname)
  ) {
    // Same container, a different sub-view. EquipmentHome resolves the fueling
    // row out of the arrays it already loaded and hands it to
    // EquipmentFuelingEntryPage, which owns its own fail-closed
    // loading/error/not-found order. Mounting that page directly here would
    // create a second data path for one record.
    return React.createElement(
      React.Suspense,
      {fallback: React.createElement(RouteLoadingFallback)},
      React.createElement(EquipmentHome),
    );
  }

  if (
    claim.resolution.surface === RELEASED_EQUIPMENT_CHECKLIST_ENTRY_SURFACE &&
    isReleasedEquipmentChecklistEntryPathname(claim.resolution.routedPathname)
  ) {
    return React.createElement(
      React.Suspense,
      {fallback: React.createElement(RouteLoadingFallback)},
      React.createElement(EquipmentHome),
    );
  }

  if (
    claim.resolution.surface === RELEASED_CATTLE_DAILYS_DETAIL_SURFACE &&
    isReleasedCattleDailysDetailPathname(claim.resolution.routedPathname)
  ) {
    // CattleDailysRouter owns the exact authenticated record hand-off. A raw
    // pathname outside this reviewed single-record form is handled by the
    // explicit route-recovery disposition rather than a loader fallback.
    return React.createElement(CattleDailysRouter);
  }

  // The two weigh-in branches share one component and differ ONLY in the
  // species prop. Each therefore re-checks its own surface AND its own raw
  // pathname before naming the species: a claim that carried the other
  // surface, or the other route, must fall through to null rather than paint
  // the sibling species' census.
  if (
    claim.resolution.surface === RELEASED_BROILER_WEIGHINS_SURFACE &&
    claim.resolution.routedPathname === RELEASED_BROILER_WEIGHINS_PATHNAME
  ) {
    return React.createElement(
      React.Suspense,
      {fallback: React.createElement(RouteLoadingFallback)},
      React.createElement(LivestockWeighInsView, {species: 'broiler'}),
    );
  }

  if (
    claim.resolution.surface === RELEASED_PIG_WEIGHINS_SURFACE &&
    claim.resolution.routedPathname === RELEASED_PIG_WEIGHINS_PATHNAME
  ) {
    return React.createElement(
      React.Suspense,
      {fallback: React.createElement(RouteLoadingFallback)},
      React.createElement(LivestockWeighInsView, {species: 'pig'}),
    );
  }

  if (
    claim.resolution.surface === RELEASED_CATTLE_WEIGHINS_SURFACE &&
    claim.resolution.routedPathname === RELEASED_CATTLE_WEIGHINS_PATHNAME
  ) {
    return React.createElement(CattleWeighInsView);
  }

  if (
    claim.resolution.surface === RELEASED_SHEEP_WEIGHINS_SURFACE &&
    claim.resolution.routedPathname === RELEASED_SHEEP_WEIGHINS_PATHNAME
  ) {
    return React.createElement(SheepWeighInsView);
  }

  if (
    claim.resolution.surface === RELEASED_SHEEP_BATCHES_INDEX_SURFACE &&
    claim.resolution.routedPathname === RELEASED_SHEEP_BATCHES_INDEX_PATHNAME
  ) {
    return React.createElement(SheepBatchesHub);
  }

  if (
    claim.resolution.surface === RELEASED_SHEEP_BATCHES_DETAIL_SURFACE &&
    isReleasedSheepBatchesDetailPathname(claim.resolution.routedPathname)
  ) {
    // The router keeps its retained hub/record split for every legacy suffix;
    // the boundary above has already admitted only the one audited record id,
    // and the router supplies the record page's client/identity props.
    return React.createElement(SheepBatchesRouter);
  }

  if (
    claim.resolution.surface === RELEASED_SHEEP_FLOCKS_INDEX_SURFACE &&
    claim.resolution.routedPathname === RELEASED_SHEEP_FLOCKS_INDEX_PATHNAME
  ) {
    return React.createElement(SheepFlocksHub);
  }

  if (
    claim.resolution.surface === RELEASED_SHEEP_FLOCKS_DETAIL_SURFACE &&
    isReleasedSheepFlocksDetailPathname(claim.resolution.routedPathname)
  ) {
    return React.createElement(SheepFlocksRouter);
  }

  if (
    claim.resolution.surface === RELEASED_TASKS_CENTER_SURFACE &&
    isReleasedTasksCenterPathname(claim.resolution.routedPathname)
  ) {
    // TasksRouter keeps its own `?task=` handoff into the still-legacy detail
    // surface; the boundary above admits only the four audited center forms.
    return React.createElement(
      UnauthorizedRedirect,
      {requireAdmin: false, fallbackView: 'home'},
      React.createElement(
        React.Suspense,
        {fallback: React.createElement(RouteLoadingFallback)},
        React.createElement(TaskCenterView),
      ),
    );
  }

  if (
    claim.resolution.surface === RELEASED_TASKS_TODO_DETAIL_SURFACE &&
    isReleasedTasksTodoDetailPathname(claim.resolution.routedPathname)
  ) {
    // The same element AppEntry mounts at `/tasks/*`: TasksRouter behind the
    // non-admin route guard. The boundary above has already proved this is the
    // exact To Do record form, so the router's own branch order lands on
    // TodoItemPage; its Task Center and task-record branches keep their
    // existing behaviour for every other path.
    return React.createElement(
      UnauthorizedRedirect,
      {requireAdmin: false, fallbackView: 'home'},
      React.createElement(
        React.Suspense,
        {fallback: React.createElement(RouteLoadingFallback)},
        React.createElement(TaskCenterView),
      ),
    );
  }

  if (
    claim.resolution.surface === RELEASED_TASKS_INSTANCE_DETAIL_SURFACE &&
    isReleasedTasksInstanceDetailPathname(claim.resolution.routedPathname)
  ) {
    // Same router, same guard: the boundary admits only one decoded-safe,
    // non-`todo` task id, and TasksRouter dispatches that to TaskInstancePage.
    return React.createElement(
      UnauthorizedRedirect,
      {requireAdmin: false, fallbackView: 'home'},
      React.createElement(
        React.Suspense,
        {fallback: React.createElement(RouteLoadingFallback)},
        React.createElement(TaskCenterView),
      ),
    );
  }

  // Character-identical to AppEntry's own `<AnimalHistoryPage />`: the route
  // element takes no props at all, so there is nothing to bridge and no second
  // mount contract is created.
  if (
    claim.resolution.surface === RELEASED_ANIMAL_HISTORY_SURFACE &&
    claim.resolution.routedPathname === RELEASED_ANIMAL_HISTORY_PATHNAME
  ) {
    return React.createElement(
      React.Suspense,
      {fallback: React.createElement(RouteLoadingFallback)},
      React.createElement(AnimalHistoryPage),
    );
  }

  // The legacy webforms route entry mounts this form as
  // `<WeighInsWebform sessionSubmitter={…} />` with App's object. The presenter
  // owns all six of its route resources already, so the released mount needs no
  // resource bridge — only the identity the form reads twice as
  // `sessionSubmitter?.name || ''`. That is the whole job of
  // WeighInsAuthenticatedBridge, next to the Add Feed and Fueling bridges that
  // answer the same question for their own surfaces.
  //
  // The legacy entry component is named in prose only, never imported: two
  // sibling release guards assert this module contains no reference to it at
  // all, because mounting it here would recreate App's whole webforms dispatch
  // behind a release claim.
  if (
    claim.resolution.surface === RELEASED_WEIGHINS_WEBFORM_SURFACE &&
    claim.resolution.routedPathname === RELEASED_WEIGHINS_WEBFORM_PATHNAME
  ) {
    return React.createElement(WeighInsAuthenticatedBridge, null, React.createElement(WeighInsWebform));
  }

  // The page directly, not a router: `/weigh-in-sessions/*` has no router and
  // no index sibling — AppEntry mounts `<WeighInSessionPage />` bare, with zero
  // props, and the page calls `useAuth` itself. The boundary above has already
  // proved this pathname carries exactly one decoded-safe segment and no
  // search or hash, so the page's own `pathname.replace(…)` id derivation
  // cannot receive the bare base or a canonicalized transition frame here.
  if (
    claim.resolution.surface === RELEASED_WEIGHIN_SESSION_RECORD_SURFACE &&
    isReleasedWeighInSessionRecordPathname(claim.resolution.routedPathname)
  ) {
    return React.createElement(
      React.Suspense,
      {fallback: React.createElement(RouteLoadingFallback)},
      React.createElement(WeighInSessionPage),
    );
  }

  // The view directly, with ZERO props, which is what the pig domain's own
  // route hands it: /pig/breeding owns its three reads through
  // pig.breeding.core and writes ppp-breeding-v1 through
  // src/lib/appStoreBlobApi.ts, so there is no persist* callback and no App
  // state setter for this mount to reproduce. It renders its own Header, as the
  // legacy mount's parent never did that for it.
  if (
    claim.resolution.surface === RELEASED_PIG_BREEDING_SURFACE &&
    claim.resolution.routedPathname === RELEASED_PIG_BREEDING_PATHNAME
  ) {
    return React.createElement(BreedingView);
  }

  // Like its domain route, FarrowingView mounts bare. Both of its persisted
  // writes use the shared app_store writer; the scheduled maintenance owner
  // publishes derived webform mirrors from persisted source, so no App callback
  // remains in the release mount.
  if (
    claim.resolution.surface === RELEASED_PIG_FARROWING_SURFACE &&
    isReleasedPigFarrowingPathname(claim.resolution.routedPathname)
  ) {
    return React.createElement(FarrowingView);
  }

  // ONE branch for BOTH of this surface's modes, deliberately. SowsView is a
  // `sub-mode` router: the same component renders the breeding-pig list at
  // /pig/sows and a record at /pig/sows/<id>, and the manifest classifies both
  // as the single surface view.sows. Splitting them here would invent a second
  // mount contract for one component. The boundary has already proved the
  // pathname is one of exactly those two shapes, so the component's own
  // prefix split cannot receive anything it does not serve. Zero props: its
  // seven reads go through pig.sows.core and its three app_store writes through
  // src/lib/appStoreBlobApi.ts.
  if (
    claim.resolution.surface === RELEASED_PIG_SOWS_SURFACE &&
    isReleasedPigSowsPathname(claim.resolution.routedPathname)
  ) {
    return React.createElement(SowsView);
  }

  // The bridge/child pair verbatim, and unlike its Pig Dailys neighbour above
  // it needs no separate mount component: TasksWebformAuthenticatedBridge
  // already derives `sessionSubmitter` from `useAuth` itself, so the legacy
  // route passes this presenter no identity prop either. The two mounts are
  // therefore byte-identical in what they hand the form, which is the property
  // that keeps the release additive rather than a second contract.
  if (
    claim.resolution.surface === RELEASED_TASKS_WEBFORM_SURFACE &&
    claim.resolution.routedPathname === RELEASED_TASKS_WEBFORM_PATHNAME
  ) {
    return React.createElement(TasksWebformAuthenticatedBridge, null, React.createElement(TasksWebform));
  }

  // Timeline now opens Add Batch through the existing batch action provider.
  // The Gantt still owns its route-local reads; opening the form hands off to
  // the fullscreen action-state bridge before its save controls can mount.
  // Named bars retain their record URLs, and the optional corrupt-name edit
  // fallback remains omitted on this released mount.
  if (
    claim.resolution.surface === RELEASED_BROILER_TIMELINE_SURFACE &&
    claim.resolution.routedPathname === RELEASED_BROILER_TIMELINE_PATHNAME
  ) {
    return React.createElement(ReleasedBroilerTimelineMount);
  }

  if (
    claim.resolution.surface === RELEASED_BROILER_FEED_SURFACE &&
    claim.resolution.routedPathname === RELEASED_BROILER_FEED_PATHNAME
  ) {
    // BroilerFeedView likewise receives no App props: its route resource owns
    // the reads, its two writers are direct, and its collapse state is local.
    return React.createElement(BroilerFeedView);
  }

  // ZERO props, and all three of PigEntry's are omitted — but only two of them
  // are omissions in the sense the BroilerDailysRouter branch above uses.
  // `setFeedOrders` and `setPigFeedInventory` publish into App state nothing
  // renders from: both feed pages read their own route resource, and neither
  // domain entry binds the values App hands it. `sbSave` was NOT in that class,
  // so it was not omitted — the physical-feed-count write moved onto
  // `saveAppStoreBlob` inside the view, with the same client and the same
  // `setSaveStatus` sink App's delegate passed. Dropping it instead would have
  // lost an operator's count with no save indicator at all.
  //
  // The former blocker was the SHARED DOCUMENT, not the props: /pig/feed and
  // /broiler/feed both live in ppp-feed-orders-v1, and a whole-document write
  // from either clobbered the other's maps. `saveFeedOrdersPatch` merges a
  // pig-only patch onto a save-time read, so the two halves are independent and
  // both feed routes can render through their own released branches.
  if (
    claim.resolution.surface === RELEASED_PIG_FEED_SURFACE &&
    claim.resolution.routedPathname === RELEASED_PIG_FEED_PATHNAME
  ) {
    return React.createElement(
      React.Suspense,
      {fallback: React.createElement(RouteLoadingFallback)},
      React.createElement(PigFeedView),
    );
  }

  return null;
}
